import { useEffect, useRef } from 'react';
import { Form, Input, Typography } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { telegram } from '../../utils/telegram';
import OnboardingFrame, { OnboardingCard, CardSectionHeader } from './OnboardingFrame';

const { Text } = Typography;

interface Props {
  value: string;
  onChange: (value: string) => void;
  onNext: () => void;
}

export default function Step1BusinessName({ value, onChange, onNext }: Props) {
  const { t } = useTranslation('owner');
  const [form] = Form.useForm();

  // Stable refs for MainButton handler.
  const formRef = useRef(form);
  formRef.current = form;
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  useEffect(() => {
    const handleNext = () => {
      formRef.current
        .validateFields()
        .then(() => onNextRef.current())
        .catch(() => {});
    };
    telegram.setMainButton(t('onboarding.mainButton.continue'), handleNext);
    return () => telegram.hideMainButton();
  }, [t]);

  return (
    <OnboardingFrame
      currentStep={1}
      badge={t('onboarding.step1.badge')}
      title={t('onboarding.step1.title')}
      subtitle={t('onboarding.step1.subtitle')}
    >
      <OnboardingCard>
        <CardSectionHeader
          icon={<ShopOutlined />}
          iconColor="#1677ff"
          title={t('onboarding.step1.fieldLabel')}
          subtitle={t('onboarding.step1.helper')}
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={{ businessName: value }}
          requiredMark={false}
        >
          <Form.Item
            name="businessName"
            style={{ marginBottom: 0 }}
            rules={[
              { required: true, message: t('onboarding.step1.validation.required') },
              { min: 2, message: t('onboarding.step1.validation.min') },
              { max: 255, message: t('onboarding.step1.validation.max') },
            ]}
          >
            <Input
              size="large"
              placeholder={t('onboarding.step1.placeholder')}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onPressEnter={() => {
                form
                  .validateFields()
                  .then(() => onNext())
                  .catch(() => {});
              }}
              autoFocus
              maxLength={255}
              style={{
                borderRadius: 12,
                fontSize: 15,
              }}
            />
          </Form.Item>
        </Form>

        <Text
          style={{
            fontSize: 11,
            color: 'var(--bl-text-muted, #9ca3af)',
            display: 'block',
            marginTop: 10,
            lineHeight: 1.4,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value.length}/255
        </Text>
      </OnboardingCard>
    </OnboardingFrame>
  );
}
